import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, PartyPopper, ShieldCheck, CalendarDays, MapPin, ArrowRight, Newspaper, Users, MessagesSquare, School, HeartHandshake, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SocialLinks from "@/components/SocialLinks";
import TreeLogo from "@/components/TreeLogo";
import EventImage from "@/components/EventImage";
import FilmSection from "@/components/FilmSection";
import RetrospectiveBanner from "@/components/RetrospectiveBanner";
import SeasonalClip, { Snowfall } from "@/components/SeasonalClip";
import SeasonalAmbience from "@/components/SeasonalAmbience";
import UsefulContactsSection from "@/components/UsefulContactsSection";
import NextEventBanner from "@/components/NextEventBanner";
import NewsTicker from "@/components/NewsTicker";
import FamilyNewsBanner from "@/components/FamilyNewsBanner";
import EventCalendar from "@/components/EventCalendar";
import api from "@/api";
import { ASSO, formatDate } from "@/constants";

const MISSIONS = [
  {
    icon: GraduationCap,
    title: "Activités socio-éducatives",
    text: "Nous concevons et organisons des activités socio-éducatives et socio-culturelles pour les enfants de l'école et leurs familles.",
  },
  {
    icon: PartyPopper,
    title: "Manifestations conviviales",
    text: "Fêtes de l'école, événements solidaires et moments de partage qui font vivre la communauté scolaire de Toury.",
  },
  {
    icon: ShieldCheck,
    title: "Défense des familles",
    text: "Nous veillons à la défense des intérêts moraux ainsi que des droits des enfants et des familles.",
  },
];

const OBJECTIFS = [
  { icon: Users, title: "Créer du lien", text: "entre les familles" },
  { icon: MessagesSquare, title: "Représenter et informer", text: "les parents" },
  { icon: School, title: "Participer activement", text: "à la vie scolaire" },
  { icon: HeartHandshake, title: "Soutenir les initiatives", text: "éducatives et citoyennes" },
  { icon: MapPin, title: "Contribuer à la vie locale", text: "et solidaire" },
  { icon: Heart, title: "Mettre du cœur", text: "dans l'école et dans la ville" },
];

const PASTEL_COLORS = ["#8FC3D4", "#F096AC", "#9CC787", "#EFD47E", "#7FB5C9", "#EB9FB4", "#A6CF92", "#F0DA8C"];

const SEASON_PALETTES = {
  halloween: { colors: ["#F59E0B", "#FB923C", "#A78BFA", "#8B5CF6", "#A3E635", "#FDE68A", "#374151", "#F97316"] },
  noel: { colors: ["#FFFFFF", "#DBEAFE", "#BFDBFE", "#F8FAFC"], snow: true },
  epiphanie: { colors: ["#FCD34D", "#F59E0B", "#FDE68A", "#FFF7ED", "#D4AF37", "#FBBF24"] },
  chandeleur: { colors: ["#E8C07D", "#D9A45B", "#F5E0B8", "#C98F45", "#FFF3D6"] },
};

const SEASONAL_META = {
  halloween: { banner: "", def: "Joyeux Halloween !", sub: "L'association vous prépare des frissons et des bonbons à Toury" },
  noel: { banner: "banner-noel", def: "Joyeux Noël !", sub: "Illuminations, féerie et gourmandises avec le CETF" },
  epiphanie: { banner: "banner-epiphanie", def: "Joyeuse Épiphanie !", sub: "Partageons la galette des rois en famille" },
  chandeleur: { banner: "banner-chandeleur", def: "Joyeuse Chandeleur !", sub: "À vos crêpes, prêts, sautez !" },
};

function buildLeaves(mode) {
  const conf = SEASON_PALETTES[mode];
  const colors = conf ? conf.colors : PASTEL_COLORS;
  const snow = !!conf?.snow;
  return Array.from({ length: 160 }, (_, i) => {
    const angle = -0.15 * Math.PI + Math.random() * 0.73 * Math.PI;
    const radius = 240 + Math.random() * 1500;
    const pumpkin = mode === "halloween" && i % 4 === 0;
    return {
      dx: Math.round(Math.cos(angle) * radius),
      dy: Math.round(Math.sin(angle) * radius * 0.5),
      rot: Math.round((Math.random() - 0.5) * 1440),
      delay: (Math.random() * (snow ? 1.2 : 0.4)).toFixed(2),
      peak: (1.7 + Math.random() * 2.3).toFixed(2),
      wind: `${Math.round(5 + Math.random() * 18)}vw`,
      size: snow ? `${6 + Math.round(Math.random() * 10)}px` : `${12 + Math.round(Math.random() * 12)}px`,
      shape: pumpkin || snow || i % 3 === 0 ? "50%" : "70% 0 70% 0",
      pumpkin,
      snow,
      dur: snow ? (3.6 + Math.random() * 1.6).toFixed(2) : (2.0 + Math.random() * 0.8).toFixed(2),
      color: pumpkin ? "#F59E0B" : colors[i % colors.length],
    };
  });
}

function TreeIntro({ mode = "none" }) {
  const [phase, setPhase] = useState("intro");
  const [dock, setDock] = useState(null);
  const wrapRef = useRef(null);
  const treeRef = useRef(null);
  const leaves = buildLeaves(mode);

  useEffect(() => {
    const compute = () => {
      const tree = treeRef.current;
      const logo = document.querySelector(".logo-tree");
      if (!tree || !logo || !wrapRef.current) return;
      const t = tree.getBoundingClientRect();
      const l = logo.getBoundingClientRect();
      wrapRef.current.style.setProperty("--dock-x", `${Math.round(l.left + l.width / 2 - (t.left + t.width / 2))}px`);
      wrapRef.current.style.setProperty("--dock-y", `${Math.round(l.top + l.height / 2 - (t.top + t.height / 2))}px`);
      wrapRef.current.style.setProperty("--dock-s", Math.max(l.height / t.height, 0.05).toFixed(3));
      setDock({ x: l.left + l.width / 2, y: l.top + l.height / 2 });
    };
    const t1 = setTimeout(compute, 1700);
    const t2 = setTimeout(() => setPhase("leaves"), 4700);
    const t3 = setTimeout(() => setPhase("done"), 8400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  if (phase === "done") return null;
  const word = "Coeur d'École – Toury Famille";
  return (
    <>
      <div
        ref={wrapRef}
        className="fixed inset-0 z-30 flex items-center justify-center pointer-events-none tree-intro-bg"
        data-testid="tree-intro"
        aria-hidden="true"
      >
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 px-4">
          <img
            ref={treeRef}
            src="/assets/tree-hd.png"
            alt=""
            className="tree-intro-tree h-[34vh] sm:h-[46vh] w-auto object-contain"
          />
          <div className="leading-tight text-center sm:text-left tree-intro-text">
          <p className="font-serif text-2xl sm:text-4xl text-slate-700" aria-label={word}>
            {word.split("").map((ch, i) => (
              <span key={i} className="tree-intro-letter" style={{ animationDelay: `${0.6 + i * 0.05}s` }} aria-hidden="true">
                {ch === " " ? " " : ch}
              </span>
            ))}
          </p>
          <p className="font-serif text-base sm:text-xl text-slate-500 mt-1 tree-intro-sub">Association — CETF —</p>
          </div>
        </div>
      </div>
      {phase === "leaves" && dock && (
        <div className="fixed z-[45] pointer-events-none" style={{ left: dock.x, top: dock.y }} data-testid="tree-leaves-burst" aria-hidden="true">
          {leaves.map((leaf, i) => (
            <span
              key={i}
              className={leaf.snow ? "snow-burst-particle" : `leaf-particle${leaf.pumpkin ? " pumpkin" : ""}`}
              style={{ "--dx": `${leaf.dx}px`, "--dy": `${leaf.dy}px`, "--rot": `${leaf.rot}deg`, "--leaf-d": `${leaf.delay}s`, "--leaf-t": `${leaf.dur}s`, "--leaf-c": leaf.color, "--leaf-size": leaf.size, "--leaf-r": leaf.shape, "--peak": leaf.peak, "--wind": leaf.wind }}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [seasonal, setSeasonal] = useState(null);
  const [introRun, setIntroRun] = useState(1);
  const [atTop, setAtTop] = useState(true);
  const [listeNavVisible, setListeNavVisible] = useState(true);
  const [homeAds, setHomeAds] = useState([]);
  const awayRef = useRef(false);

  useEffect(() => {
    api.get("/events").then((r) => {
      const today = new Date(new Date().toDateString());
      setEvents(r.data.filter((e) => e.date && new Date(e.date) >= today).slice(0, 3));
    }).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api.get("/seasonal").then((r) => setSeasonal(r.data)).catch(() => {});
    api.get("/liste-cetf/public-info").then((r) => {
      if (r.data?.nav_visible === false) {
        setListeNavVisible(false);
        api.get("/nav-ads").then((res) => setHomeAds(res.data)).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const top = window.scrollY <= 10;
      setAtTop(top);
      if (!top) {
        awayRef.current = true;
      } else if (awayRef.current) {
        awayRef.current = false;
        setIntroRun((n) => n + 1);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="home-page">
      <div className="home-watermark" style={{ backgroundImage: "url(/assets/logo-tree.png)" }} aria-hidden="true" />
      {atTop && <TreeIntro key={introRun} mode={seasonal?.mode || "none"} />}
      {seasonal?.mode && seasonal.mode !== "none" && SEASONAL_META[seasonal.mode] && (
        <div className={`theme-keep halloween-banner ${SEASONAL_META[seasonal.mode].banner}`} data-testid="seasonal-banner">
          <p className="halloween-title" data-testid="seasonal-banner-title">{seasonal.seasonal_text || SEASONAL_META[seasonal.mode].def}</p>
          <p className="halloween-sub">{SEASONAL_META[seasonal.mode].sub}</p>
        </div>
      )}
      {atTop && seasonal && ["noel", "epiphanie", "chandeleur"].includes(seasonal.mode) && (
        <SeasonalClip key={`clip-${introRun}`} mode={seasonal.mode} />
      )}
      {seasonal?.mode === "noel" && <Snowfall persistent count={120} />}
      {seasonal && seasonal.mode !== "none" && <SeasonalAmbience mode={seasonal.mode} />}
      <FilmSection />
      <RetrospectiveBanner />
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="flex items-center gap-4 animate-fade-up" data-testid="hero-logo-lockup">
              <TreeLogo className="h-28 sm:h-36 shrink-0" />
              <div className="leading-tight">
                <p className="font-serif text-xl sm:text-2xl text-slate-700">Coeur d'École – Toury Famille</p>
                <p className="font-serif text-sm sm:text-base text-slate-500">Association — CETF —</p>
                <p className="text-xs text-slate-400 mt-1.5">Touchez l'arbre : les feuilles s'envolent vers le soleil et repoussent</p>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-slate-900 animate-fade-up-1" data-testid="hero-title">
              Ensemble, faisons grandir nos enfants
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 animate-fade-up-2" data-testid="hero-description">
              {ASSO.description}
            </p>
            <div className="flex flex-col items-start gap-3.5 animate-fade-up-3">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" asChild data-testid="hero-adherer-btn">
                <a href={ASSO.helloassoUrl} target="_blank" rel="noopener noreferrer">
                  Adhérer — 10 €/an
                </a>
              </Button>
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold shadow-md hover:shadow-lg transition-shadow" asChild data-testid="hero-don-btn">
                <a href={ASSO.helloassoDonateUrl} target="_blank" rel="noopener noreferrer">
                  <img src={ASSO.logoTreeUrl} alt="" className="h-6 w-6 object-contain mr-0.5" />
                  Faire un don libre à CETF
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild data-testid="hero-decouvrir-btn">
                <Link to="/qui-sommes-nous">
                  Découvrir l'association <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
          <div className="relative animate-fade-up-2">
            <div className="absolute -top-6 -left-6 w-40 h-40 bg-emerald-100 rounded-full blur-2xl opacity-70" aria-hidden="true" />
            <div className="absolute -bottom-8 -right-4 w-52 h-52 bg-sky-100 rounded-full blur-2xl opacity-70" aria-hidden="true" />
            <div className="relative flex justify-end mb-3">
              <Link to="/evenements" className="inline-flex items-center text-sm font-semibold text-sky-700 hover:text-sky-800 hover:underline" data-testid="hero-all-events-link">
                Voir tous nos événements <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
            <NextEventBanner />
          </div>
        </div>
      </section>

      <FamilyNewsBanner />

      <section className="py-12 sm:py-16 lg:py-20 bg-white border-b border-slate-100" data-testid="home-calendar-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">S'organiser</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Calendrier & vacances scolaires</h2>
              <p className="text-sm sm:text-base text-slate-600 mt-3">
                Nos événements, les vacances scolaires de la Zone B, les jours fériés et les dates importantes — tout au même endroit.
              </p>
            </div>
            <Link to="/evenements" className="inline-flex items-center text-sm font-semibold text-sky-700 hover:text-sky-800 hover:underline shrink-0" data-testid="calendar-all-events-link">
              Voir tous nos événements <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
          <EventCalendar />
        </div>
      </section>

      {listeNavVisible ? (
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white" data-testid="home-liste-cetf-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300 mb-2">Élections de parents d'élèves</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Liste CET — portez la voix des familles</h2>
            <p className="text-sm sm:text-base text-emerald-100/90 mt-3 leading-relaxed">
              Des parents d'élèves de l'école élémentaire Julien Mercier de Toury constituent leur propre liste —
              une liste de parents (association de fait), avec son nom et son logo, <strong>soutenue par</strong> l'association
              Cœur d'École Toury Famille. Envie de vous engager ? Déposez votre candidature en quelques minutes.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold" asChild data-testid="home-liste-cetf-cta">
                <Link to="/liste-cetf">Déposer ma candidature <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
              <Button variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white bg-transparent" asChild>
                <Link to="/liste-cetf">Découvrir la liste</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      ) : homeAds.length > 0 && (
      <section className="py-10 sm:py-14 bg-white border-b border-slate-100" data-testid="home-ads-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2 text-center">Publicité</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 text-center">Nos amis</h2>
          <p className="text-sm text-slate-500 text-center mt-2">Ils soutiennent l'association — merci à eux !</p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 mt-8">
            {homeAds.map((ad) => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/nav-ads-media/${ad.storage_path}`;
              const media = ad.media_type === "video" ? (
                <video src={url} autoPlay loop muted playsInline className="h-20 sm:h-24 w-auto max-w-[70vw] object-contain" />
              ) : (
                <img src={url} alt={ad.title || "Nos amis"} className="h-20 sm:h-24 w-auto max-w-[70vw] object-contain" />
              );
              return (
                <figure key={ad.id} className="flex flex-col items-center gap-1.5" data-testid={`home-ad-${ad.id}`}>
                  {ad.link ? (
                    <a href={ad.link} target="_blank" rel="noopener noreferrer" data-testid={`home-ad-link-${ad.id}`}>{media}</a>
                  ) : (
                    media
                  )}
                  {ad.title && <figcaption className="text-xs font-medium text-slate-500">{ad.title}</figcaption>}
                </figure>
              );
            })}
          </div>
        </div>
      </section>
      )}

      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-emerald-50/80 to-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Notre mission</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Ce que nous faisons, concrètement</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {MISSIONS.map(({ icon: Icon, title, text }, i) => (
              <Card key={title} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`mission-card-${i}`}>
                <CardContent className="p-6 sm:p-8 space-y-4">
                  <div className="h-12 w-12 rounded-2xl bg-sky-100 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-sky-700" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-slate-800">{title}</h3>
                  <p className="text-sm sm:text-base leading-relaxed text-slate-600">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-14 grid lg:grid-cols-5 gap-8 items-center">
            <div className="lg:col-span-2 bg-white rounded-3xl border border-emerald-100 shadow-sm p-8" data-testid="mission-quote">
              <p className="font-serif text-xl sm:text-2xl text-slate-700 leading-relaxed">
                « Faire grandir les enfants, c'est aussi faire grandir les liens qui nous unissent. »
              </p>
              <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                Nous œuvrons chaque jour pour le bien-être des enfants et le soutien aux familles,
                tout en participant à la dynamique de la commune.
              </p>
            </div>
            <div className="lg:col-span-3">
              <h3 className="text-xl font-semibold text-slate-800 mb-5">Nos objectifs au quotidien</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {OBJECTIFS.map(({ icon: Icon, title, text }, i) => (
                  <div key={title} className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 px-5 py-4 hover:shadow-md transition-shadow" data-testid={`objectif-${i}`}>
                    <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-emerald-700" />
                    </div>
                    <p className="text-sm text-slate-700"><span className="font-semibold">{title}</span> {text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsTicker />

      <section className="py-12 sm:py-16 lg:py-20 bg-white border-y border-slate-200" data-testid="home-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Agenda</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Événements à venir</h2>
            </div>
            <Button variant="ghost" asChild data-testid="home-events-all-btn">
              <Link to="/evenements" className="text-sky-700">Tout voir <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-slate-500 bg-slate-50 rounded-xl p-6" data-testid="home-events-empty">
              Aucun événement public programmé pour le moment — revenez bientôt !
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map((e) => (
                <Card key={e.id} className="border-slate-200 hover:shadow-md transition-shadow overflow-hidden" data-testid={`home-event-card-${e.id}`}>
                  <CardContent className="p-6 space-y-3">
                    {e.image_url && (
                      <EventImage src={e.image_url} alt={`Affiche ${e.title}`}
                        className="w-full aspect-[4/3] object-cover rounded-xl -mt-1 mb-1 shadow-sm"
                        testId={`home-event-image-${e.id}`} />
                    )}
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">
                      <CalendarDays className="h-3 w-3 mr-1" />{formatDate(e.date)}
                    </Badge>
                    <h3 className="text-lg font-semibold text-slate-800">{e.title}</h3>
                    {e.location && (
                      <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.location}</p>
                    )}
                    <p className="text-sm text-slate-600 line-clamp-3">{e.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-sky-50/60" data-testid="home-contacts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Vie locale</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Adresses & numéros utiles</h2>
              <p className="text-sm sm:text-base text-slate-600 mt-3">
                Les contacts essentiels autour de la vie scolaire et locale à Toury.
              </p>
            </div>
            <Button variant="ghost" asChild data-testid="home-contacts-all-btn">
              <Link to="/adresses-utiles" className="text-sky-700 shrink-0">Tout voir <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </div>
          <UsefulContactsSection />
        </div>
      </section>

      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-sky-50/90 to-white" data-testid="home-news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">Vie de l'association</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Dernières actualités</h2>
            </div>
            <Button variant="ghost" asChild data-testid="home-news-all-btn">
              <Link to="/actualites" className="text-sky-700">Tout voir <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </div>
          {news.length === 0 ? (
            <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-6" data-testid="home-news-empty">
              Aucune actualité publiée pour le moment.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((n) => (
                <Card key={n.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`home-news-card-${n.id}`}>
                  <CardContent className="p-6 space-y-3">
                    <Badge className="bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100">
                      <Newspaper className="h-3 w-3 mr-1" />{formatDate(n.created_at)}
                    </Badge>
                    <h3 className="text-lg font-semibold text-slate-800">{n.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-3">{n.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-slate-900" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="space-y-3 max-w-xl">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Envie de vous investir ?</h2>
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
              Rejoignez l'association pour participer aux décisions, accéder aux comptes rendus
              et contribuer aux activités des enfants. L'adhésion coûte 10 € par an et par famille.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" asChild data-testid="cta-adherer-btn">
                <a href={ASSO.helloassoUrl} target="_blank" rel="noopener noreferrer">Adhérer en ligne</a>
              </Button>
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 hover:text-white" asChild data-testid="cta-contact-btn">
                <Link to="/contact">Nous contacter</Link>
              </Button>
            </div>
            <div className="flex items-center gap-4" data-testid="cta-socials">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Suivez-nous</span>
              <SocialLinks />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
